import Tracking from '/builds/gitlab/gitlab-ee/app/assets/javascripts/tracking/index.js?vue3';
import { getIdFromGraphQLId } from '~/graphql_shared/utils';
import { visitUrl } from '~/lib/utils/url_utility';
import { sprintf, __ } from '~/locale';
import workItemTypesConfigurationQuery from '~/work_items/graphql/work_item_types_configuration.query.graphql';
import setActiveBoardItemMutation from 'ee_else_ce/boards/graphql/client/set_active_board_item.mutation.graphql';
import activeBoardItemQuery from 'ee_else_ce/boards/graphql/client/active_board_item.query.graphql';
import { glSlotsMixin } from '~/lib/utils/vue3compat/gl_slots_mixin';
import BoardCardInner from '/builds/gitlab/gitlab-ee/app/assets/javascripts/boards/components/board_card_inner.vue?vue3';
export default {
    name: 'BoardCard',
    components: {
        BoardCardInner
    },
    mixins: [
        Tracking.mixin(),
        glSlotsMixin
    ],
    inject: [
        'disabled',
        'fullPath',
        'isIssueBoard',
        'isEpicBoard'
    ],
    props: {
        list: {
            type: Object,
            default: ()=>({}),
            required: false
        },
        item: {
            type: Object,
            default: ()=>({}),
            required: false
        },
        index: {
            type: Number,
            default: 0,
            required: false
        },
        showWorkItemTypeIcon: {
            type: Boolean,
            default: false,
            required: false
        },
        canAdmin: {
            type: Boolean,
            required: false,
            default: true
        },
        columnIndex: {
            type: Number,
            required: false,
            default: 0
        },
        rowIndex: {
            type: Number,
            required: false,
            default: 0
        }
    },
    emits: [
        'set-filters'
    ],
    data () {
        return {
            activeBoardItem: undefined,
            workItemTypesConfiguration: []
        };
    },
    apollo: {
        activeBoardItem: {
            query: activeBoardItemQuery,
            variables () {
                return {
                    isIssue: this.isIssueBoard
                };
            }
        },
        workItemTypesConfiguration: {
            query: workItemTypesConfigurationQuery,
            variables () {
                return {
                    fullPath: this.fullPath
                };
            },
            update (data) {
                return data?.namespace?.workItemTypes?.nodes || [];
            }
        }
    },
    computed: {
        activeItemId () {
            return this.activeBoardItem?.id;
        },
        isActive () {
            return this.item.id === this.activeItemId;
        },
        isDisabled () {
            return this.disabled || !this.item.id || this.item.isLoading || !this.canAdmin;
        },
        isDraggable () {
            return !this.isDisabled;
        },
        itemColor () {
            return this.item.color;
        },
        cardStyle () {
            return this.itemColor ? {
                borderLeftColor: this.itemColor
            } : '';
        },
        formattedItem () {
            return {
                ...this.item,
                assignees: this.item.assignees?.nodes || [],
                labels: this.item.labels?.nodes || []
            };
        },
        showFocusBackground () {
            return !this.isActive;
        },
        itemPrefix () {
            return this.isEpicBoard ? '&' : '#';
        },
        itemReferencePath () {
            const { referencePath } = this.item;
            return referencePath.split(this.itemPrefix)[0];
        },
        boardItemUniqueId () {
            return `listItem-${this.itemReferencePath}/${getIdFromGraphQLId(this.item.id)}`;
        }
    },
    methods: {
        toggleIssue (e) {
            // Don't do anything if this happened on a no trigger element
            if (e.target.closest('.js-no-trigger')) {
                e.preventDefault();
                return;
            }
            // Allow Ctrl/Cmd+click to open link in new tab
            const isMetaKey = e.ctrlKey || e.metaKey;
            if (isMetaKey) {
                // Let the browser handle the new tab open
                return;
            }
            e.preventDefault();
            const type = this.workItemTypesConfiguration.find(({ id })=>id === this.item.workItemType?.id);
            // Legacy types need to be shown in the legacy view until they're migrated to work items
            if (type?.useIssueView) {
                visitUrl(this.item.webPath);
                return;
            }
            this.$el.querySelector('.board-card-button')?.focus();
            this.toggleItem();
            this.track('click_card', {
                label: 'right_sidebar'
            });
        },
        async toggleItem () {
            this.$apollo.mutate({
                mutation: setActiveBoardItemMutation,
                variables: {
                    boardItem: this.isActive ? null : this.item,
                    listId: this.list.id,
                    isIssue: this.isIssueBoard
                }
            });
        },
        openWorkItemPanel () {
            this.$apollo.mutate({
                mutation: setActiveBoardItemMutation,
                variables: {
                    boardItem: this.item,
                    listId: this.list.id,
                    isIssue: this.isIssueBoard
                }
            });
        },
        changeFocusInColumn (currentCard, i) {
            // Building a list using data-col-index instead of just traversing the ul is necessary for swimlanes
            const columnCards = [
                ...document.querySelectorAll(`a.board-card-button[data-col-index="${this.columnIndex}"]`)
            ];
            const currentIndex = columnCards.indexOf(currentCard);
            if (currentIndex + i < 0 || currentIndex + i > columnCards.length - 1) {
                return;
            }
            columnCards[currentIndex + i].focus();
        },
        focusNext (e) {
            this.changeFocusInColumn(e.target, 1);
        },
        focusPrev (e) {
            this.changeFocusInColumn(e.target, -1);
        },
        changeFocusInRow (currentCard, i) {
            const currentList = currentCard.closest('ul');
            // Find next in line list/cell with cards. If none, don't move.
            let listSelector = 'board-list';
            // Account for swimlanes using different structure. Swimlanes traverse within their lane.
            if (currentList.classList.contains('board-cell')) {
                listSelector = `board-cell[data-row-index="${this.rowIndex}"]`;
            }
            const lists = [
                ...document.querySelectorAll(`ul.${listSelector}:not(.list-empty):not(.list-collapsed)`)
            ];
            const currentIndex = lists.indexOf(currentList);
            if (currentIndex + i < 0 || currentIndex + i > lists.length - 1) {
                return;
            }
            // Focus the same index if possible, or last card
            const targetCards = lists[currentIndex + i].querySelectorAll('a.board-card-button');
            if (targetCards.length <= this.index) {
                targetCards[targetCards.length - 1].focus();
            } else {
                targetCards[this.index].focus();
            }
        },
        focusLeft (e) {
            this.changeFocusInRow(e.target, -1);
        },
        focusRight (e) {
            this.changeFocusInRow(e.target, 1);
        },
        createIssueLabelwithNumber () {
            return sprintf(__(`Issue number %{itemIid}: %{title}`), {
                itemIid: this.item.iid,
                title: this.item.title
            });
        }
    }
};
